// TODO: need to cleanup and refactor this file after refactor add dataset from useConfiguration has been tested well

import { useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry
} from "geojson";

import { getDatasetMetadata } from "@/api/dataset/datasetApi";
import {
  GEOHASH_RESOLUTION,
  H3_RESOLUTION
} from "@/components/spatialAnalysis/spatialOutputAnalysis/data";
import { MESSAGE } from "@/constants/message";
import { buildFilterFromResponse } from "@/helpers/filterHelper";
import { getIndexOfHighestValue } from "@/lib/utils";
import {
  initialCatchmentSetting,
  setSpatialState,
  spatialInitialState,
  SpatialState
} from "@/store/slice/spatialSlice";
import {
  EspatialAggregationType,
  type SpatialAggregationType
} from "@/types/common";
import {
  DataSchemaType,
  type DatasetAttributeSchema,
  DatasetData,
  type DatasetDetailResponse,
  type DatasetDetailSchemaResponse,
  type DatasetFilter,
  WeightValueEntry
} from "@/types/dataset";
import {
  AnalysisConfigurationDetail,
  DatasetConfigResponse
} from "@/types/project";
import {
  AreaInputType,
  CatchmentDatasetConfig,
  CatchmentSettings,
  CatchmentTransport,
  ECatchmentInputBy,
  ECatchmentType,
  EOutputAnalysisType,
  EOutputGridType,
  ERoadAccessMeasurement,
  ERoadExclusions,
  IsochroneData,
  OutputGridType,
  PolygonSettingItem,
  ScoringOptionsEnum
} from "@/types/spatial";
import { generateDatasetId } from "@/utils/common";
import { createNewDataset } from "@/utils/dataset";

import { area, circle } from "@turf/turf";
import { v4 as uuidv4 } from "uuid";

import { useMap } from "./map/core/useMapContext";
import { useDataset } from "./map/useDataset";
import useToast, { EToastType } from "./ui/useToast";

interface UseAnalysisConfigurationProps {
  configurationDetail?: AnalysisConfigurationDetail;
  onGoBack?: () => void;
}

const mapSpatialConfig = (
  configurationDetail: AnalysisConfigurationDetail,
  areaSize: number
) => {
  const outputType = configurationDetail.analysis_output?.type;
  const isProfiling = outputType === EOutputAnalysisType.PROFILING;
  const isH3 = outputType === EOutputGridType.H3;
  const isGeoHash = outputType === EOutputGridType.GEOHASH;
  const areaInput = configurationDetail.area_input;
  let areaInputOption = null;

  if (areaInput) {
    if (areaInput.adm_area_id) {
      areaInputOption = {
        label: "Administrative Area",
        value: AreaInputType.ADMIN_AREA
      };
    } else if (areaInput.custom_catchments) {
      areaInputOption = {
        label: "Polygon",
        value: AreaInputType.POLYGON
      };
    } else {
      areaInputOption = { label: "Catchment", value: AreaInputType.CATCHMENT };
    }
  }

  let resolvedOutputType: EOutputAnalysisType | undefined;
  if (!outputType) {
    resolvedOutputType = undefined;
  } else if (isProfiling) {
    resolvedOutputType = EOutputAnalysisType.PROFILING;
  } else {
    resolvedOutputType = EOutputAnalysisType.GRID;
  }

  return {
    areaInput: areaInputOption,
    outputType: resolvedOutputType,
    gridType: configurationDetail.analysis_output?.type as OutputGridType,
    gridResolution: isGeoHash
      ? GEOHASH_RESOLUTION.find(
          (geo) =>
            geo.value ===
            configurationDetail.analysis_output?.grid_level?.toString()
        )
      : null,
    h3Resolution: isH3
      ? H3_RESOLUTION.find(
          (geo) =>
            geo.value ===
            configurationDetail.analysis_output?.grid_level?.toString()
        )
      : null,
    areaSize,
    scoringOption:
      configurationDetail.analysis_output?.scoring_option ??
      ScoringOptionsEnum.RAW
  };
};

export const createDatasetConfig = (
  data: DatasetData,
  detail: DatasetDetailResponse,
  datasetConfig: DatasetConfigResponse
): CatchmentDatasetConfig => {
  const { id, nextIndex } = generateDatasetId(data.dataset_id, {});
  const datasetName =
    nextIndex > 1 ? `${data.name} (${nextIndex - 1})` : data.name;
  const newDataset = createNewDataset({
    dataset: detail,
    datasetId: id,
    datasetName: datasetName,
    attributesSchema: detail.schema as unknown as DatasetAttributeSchema[],
    styleConfig: {
      selectedCategory: datasetConfig.color_based,
      labelConfig: datasetConfig.label_config,
      rankConfig: {
        isEnabled: datasetConfig.label_config?.enable_ranking ?? false,
        maxRanking: datasetConfig.label_config?.max_ranking ?? 10
      }
    }
  });

  return {
    datasets: {
      [id]: {
        dataset: newDataset,
        rawDataset: data,
        filters: mapDatasetFilters(datasetConfig, detail.schema)
      }
    },
    visualizationOrder: [id],
    datasetIndices: {
      [data.dataset_id]: {
        counter: 1,
        usedIndices: [1]
      }
    },
    selectedDataset: null
  };
};

const mapCatchmentSettings = (
  configurationDetail: AnalysisConfigurationDetail
): CatchmentSettings | null => {
  const catchment = configurationDetail.area_input?.catchment;
  if (!catchment) return null;

  const isDistance = catchment.metric === ERoadAccessMeasurement.DISTANCE;
  const isDuration = catchment.metric === ERoadAccessMeasurement.DURATION;
  const contours = catchment.contours.map(String);

  return {
    catchmentInputBy:
      catchment.locations && !catchment.dataset
        ? ECatchmentInputBy.BY_LOCATION
        : ECatchmentInputBy.BY_DATASET,
    radiusData: catchment.profile === ECatchmentType.BY_RADIUS ? contours : [],
    catchmentType:
      catchment.profile === ECatchmentType.BY_RADIUS
        ? ECatchmentType.BY_RADIUS
        : ECatchmentType.BY_ROAD,
    placeCoordinates: catchment.dataset
      ? undefined
      : catchment.locations?.map((place) => ({
          lng: place.coordinates.longitude,
          lat: place.coordinates.latitude,
          locationName: place.name,
          id: uuidv4()
        })),
    roadAccessConfig: {
      routingType: catchment.profile as CatchmentTransport,
      measurementType: catchment.metric as ERoadAccessMeasurement,
      roadExclusions: catchment.exclusions as ERoadExclusions[],
      distances: isDistance ? contours : [],
      durations: isDuration ? contours : [],
      isochroneData:
        catchment.locations && catchment.locations?.length > 0
          ? (catchment.locations.map((location, position) => ({
              position,
              data: location.catchment_area ?? [] // safe fallback to empty array
            })) as IsochroneData[])
          : []
    },
    ...(configurationDetail.catchmentDatasetData &&
    configurationDetail.catchmentDatasetDetail &&
    catchment.dataset?.color_based
      ? {
          datasetConfig: createDatasetConfig(
            configurationDetail.catchmentDatasetData,
            configurationDetail.catchmentDatasetDetail,
            catchment.dataset
          )
        }
      : {})
  };
};

export const mapSpatialState = (
  configurationDetail: AnalysisConfigurationDetail,
  addFeature: (feature: Feature) => string | string[] | undefined,
  analysisId?: string
): SpatialState => {
  let calculatedAreaSize = 0;
  const adminGeoData = configurationDetail.adminAreaData?.geoData;
  const catchmentSettings = mapCatchmentSettings(configurationDetail);
  const polygonSettings: Record<string, PolygonSettingItem> = {};

  // check if admin area or not
  if (configurationDetail.area_input?.adm_area_id) {
    calculatedAreaSize = area(
      adminGeoData?.data[0].boundary as FeatureCollection
    );
  } else if (configurationDetail.area_input?.catchment) {
    if (
      catchmentSettings?.catchmentType === ECatchmentType.BY_RADIUS &&
      catchmentSettings.radiusData
    ) {
      const maxRadius = Math.max(
        ...catchmentSettings.radiusData.map((radius) => parseInt(radius))
      );
      const areaCircle = circle([0, 0], maxRadius, {
        steps: 64,
        units: "meters"
      });
      calculatedAreaSize = area(areaCircle);
    } else if (
      catchmentSettings?.catchmentType === ECatchmentType.BY_ROAD &&
      catchmentSettings.roadAccessConfig?.isochroneData
    ) {
      const roadAccessConfig = catchmentSettings.roadAccessConfig;
      const isochroneData = roadAccessConfig.isochroneData;

      const type =
        roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE
          ? (roadAccessConfig?.distances ?? [""])
          : (roadAccessConfig?.durations ?? [""]);

      const index = getIndexOfHighestValue(type ?? []);
      const highestIsochrone = isochroneData?.map(
        (data: { position: number; data: Array<FeatureCollection> }) => {
          return data.data[index];
        }
      );

      const isochroneAreas =
        highestIsochrone?.map((data) => {
          if (!data) return 0;
          return area(data);
        }) ?? [];

      calculatedAreaSize = Math.max(...isochroneAreas);
    }
  } else if (configurationDetail.area_input?.custom_catchments) {
    configurationDetail.area_input?.custom_catchments.features.forEach(
      (feature, index) => {
        const id = uuidv4();

        const customFeature: Feature<Geometry, GeoJsonProperties> = {
          ...feature,
          properties: {
            ...feature.properties,
            sequence: index + 1
          },
          id
        };

        polygonSettings[id] = {
          polygon: customFeature,
          name: customFeature.properties?.name ?? "",
          areaSize: customFeature.properties?.area_size ?? 0,
          sequence: index,
          id: id
        };

        addFeature(customFeature);
      }
    );
  }
  return {
    ...spatialInitialState,
    isEdit: !!configurationDetail.id,
    currentConfigId: configurationDetail.id,
    title: configurationDetail.name,
    description: configurationDetail.description,
    adminAreaSettings: configurationDetail.adminAreaData?.selectValue ?? {
      lv0: null,
      lv1: null,
      lv2: null,
      lv3: null,
      lv4: null
    },
    spatialConfig: mapSpatialConfig(configurationDetail, calculatedAreaSize),
    polygonSettings: polygonSettings,
    polygonSettingsTemp: {},
    catchmentSettings: catchmentSettings,
    catchmentSettingsTemp: initialCatchmentSetting,
    adminAreaSettingsData: configurationDetail.adminAreaData?.geoData ?? null,
    adminAreaSettingsDataTemp:
      configurationDetail.adminAreaData?.geoData ?? null,
    isShowMarkerLabel:
      configurationDetail.area_input?.catchment?.dataset?.label_config
        ?.show_label ?? false,
    latestAnalysisId: analysisId,
    overrideGlobalFilter: !configurationDetail.area_input?.is_global_admin_area
  };
};

const mapDatasetFilters = (
  datasetOriginal: DatasetConfigResponse,
  schema: DatasetDetailSchemaResponse[]
): DatasetFilter[] => {
  if (!datasetOriginal?.attributes?.length) return [];

  const getPosition = (filterId: string, attributeCode: string = "") =>
    schema.find(
      (item) => item.id === filterId && item.attribute_code === attributeCode
    )?.position;

  return datasetOriginal.attributes
    .map<DatasetFilter | null>((attribute) => {
      if (!attribute?.filter?.criteria) return null;

      const isAdminArea =
        attribute.attribute_code?.startsWith("adm_area_level_");

      // When saving, paired_key_attribute_id is used as the attributeId.
      // When opening, we need to find the schema item that has this paired_key_attribute_id
      // and use its actual id instead.
      const schemaItemByPairedKey = schema.find(
        (item) =>
          item.paired_key_attribute_id === attribute.id &&
          item.attribute_code === attribute.attribute_code
      );

      // If not found by paired_key, fallback to direct id match
      const schemaItem =
        schemaItemByPairedKey ??
        schema.find(
          (item) =>
            item.id === attribute.id &&
            item.attribute_code === attribute.attribute_code
        );

      // Use the schema item's actual id if found via paired_key lookup
      const resolvedAttributeId = schemaItemByPairedKey
        ? schemaItemByPairedKey.id
        : attribute.id;

      return {
        ...buildFilterFromResponse({
          criteria: attribute.filter.criteria,
          include_nulls: attribute.filter.include_nulls
        }),
        ...(isAdminArea
          ? {}
          : { attributeCode: attribute.attribute_code || "" }),
        filterId: `${resolvedAttributeId}_${getPosition(resolvedAttributeId, attribute.attribute_code || "")}`,
        attributeId: resolvedAttributeId,
        paired_key_attribute_id: schemaItem?.paired_key_attribute_id ?? null,
        filterLabel: schemaItem?.attribute_name ?? ""
      };
    })
    .filter((filter): filter is DatasetFilter => filter !== null);
};

export const mapDatasetWeights = (
  datasetOriginal: DatasetConfigResponse,
  datasetSchema: DatasetDetailSchemaResponse[]
): Record<string, WeightValueEntry> => {
  if (!datasetOriginal) return {};
  const result: Record<string, WeightValueEntry> = {};
  if (!datasetOriginal?.attributes?.length) return result;

  // For dataset with "POI" spatial type, we only have 1 attribute called "Total POI Count"
  if (datasetOriginal?.spatial_aggregation_type === "poi") {
    const poiAttribute = datasetOriginal.attributes[0];
    if (!poiAttribute) return result;

    const entry: WeightValueEntry = {
      weight: String(poiAttribute.weight_score ?? 0)
    };

    if (
      poiAttribute.weight_negative_impact !== undefined &&
      poiAttribute.weight_negative_impact !== null
    ) {
      entry.negative_impact = poiAttribute.weight_negative_impact;
    }

    result[poiAttribute.id] = entry;
    return result;
  }

  // Create map to hold schema data types
  const schemaDataTypeMap = new Map<string, DataSchemaType>();
  for (const schemaItem of datasetSchema) {
    schemaDataTypeMap.set(
      schemaItem.id,
      schemaItem.data_type as DataSchemaType
    );
  }

  for (const attribute of datasetOriginal.attributes) {
    const dataType = schemaDataTypeMap.get(attribute.id);

    const isDataTypeNumeric =
      dataType === DataSchemaType.INT || dataType === DataSchemaType.FLOAT;

    const isDataWeightValid =
      attribute?.weight_score !== undefined &&
      attribute?.weight_score !== null &&
      !isNaN(Number(attribute.weight_score));

    if (!isDataTypeNumeric || !isDataWeightValid) {
      continue; // Skip this attribute and move to the next
    }
    const entry: WeightValueEntry = {
      weight: String(attribute.weight_score)
    };

    if (
      attribute.weight_negative_impact !== undefined &&
      attribute.weight_negative_impact !== null
    ) {
      entry.negative_impact = attribute.weight_negative_impact;
    }

    result[attribute.id] = entry;
  }

  return result;
};

export const getDeletedDatasetsName = (datasets: DatasetConfigResponse[]) => {
  if (!datasets || datasets.length === 0) return null;
  /** Get the deleted dataset's names, should only return once per dataset if the deleted dataset is used multiple times in the configuration */
  const uniqueDatasetsById = new Set<string>();
  const names =
    datasets
      .filter(
        (dataset) =>
          !dataset.available &&
          !uniqueDatasetsById.has(dataset.id) &&
          uniqueDatasetsById.add(dataset.id)
      )
      .map((dataset) => dataset.name) ?? [];
  if (!names.length) return null;
  return names.join(", ");
};

export const useAnalysisConfiguration = ({
  configurationDetail,
  onGoBack
}: UseAnalysisConfigurationProps) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { addFeature } = useMap();
  const { handleAddDatasetAndWeightValues, handleAddDeletedDataset } =
    useDataset();

  const getIndexOfDatasetSameId = (datasetId: string, index: number) => {
    if (!configurationDetail) {
      return 0;
    }
    const numberItemSameBefore =
      [...configurationDetail.datasets]
        .reverse()
        .slice(0, index)
        ?.filter((dataset) => dataset.id === datasetId) ?? [];
    return numberItemSameBefore?.length;
  };

  const processDatasets = async (
    configurationDetail: AnalysisConfigurationDetail
  ) => {
    const reversedDatasets = [...configurationDetail.datasets].reverse();
    const reversetDatasetsData = [
      ...configurationDetail.datasetsData
    ].reverse();
    const reversedDatasetsDetail = [
      ...configurationDetail.datasetsDetail
    ].reverse();
    // Iterate in reverse order to maintain the correct indexing
    // The first datasets need to be added last to ensure the correct dataset visual order
    for (const [index, dataset] of reversedDatasets.entries()) {
      // A configuration can use the same dataset multiple times.
      // We need to determine which instance of the dataset this is (e.g., the 1st, 2nd, etc.).
      const datasetIndex = getIndexOfDatasetSameId(dataset.id, index) + 1;
      // Convert the 1-based datasetIndex to a 0-based occurrenceIndex for array access.
      const occurrenceIndex = datasetIndex - 1;

      if (dataset.available) {
        // Since a dataset ID can be duplicated, .find() is unreliable as it only returns the first match.
        // Instead, we .filter() to get all records matching the ID...
        const matchingDatasets = reversetDatasetsData.filter(
          (datasetData) => datasetData.dataset_id === dataset.id
        );
        // ...and then use the occurrenceIndex to select the correct data record for this specific instance.
        const matchedDatasetData = matchingDatasets[occurrenceIndex];

        // The same logic applies to finding the correct metadata.
        const matchingMetadata = reversedDatasetsDetail.filter(
          (datasetMetadata) => datasetMetadata.id === dataset.id
        );
        const datasetMetadata = matchingMetadata[occurrenceIndex];

        if (matchedDatasetData && datasetMetadata) {
          let datasetSchema = datasetMetadata?.schema;
          // For H3 dataset, we need to get the aggregated schema
          if (
            datasetMetadata?.spatial_aggregation_type ===
              EspatialAggregationType.H3 &&
            datasetMetadata?.source === "bvt"
          ) {
            const aggregatedDatasetMetadata = await getDatasetMetadata(
              datasetMetadata?.id,
              {
                aggregationType: "AVG"
              }
            );
            if (aggregatedDatasetMetadata?.schema) {
              datasetSchema = aggregatedDatasetMetadata?.schema;
            }
          }

          // Use raw schema for the filter, instead of the aggregated one
          const filters = mapDatasetFilters(
            dataset,
            datasetMetadata?.schema ?? []
          );

          const weights = mapDatasetWeights(dataset, datasetMetadata?.schema);

          handleAddDatasetAndWeightValues(
            {
              ...datasetMetadata,
              display_name: dataset?.display_name ?? dataset.name
            },
            datasetIndex,
            matchedDatasetData,
            filters,
            datasetSchema as DatasetAttributeSchema[],
            weights,
            {
              colorFamily: dataset?.color_scale,
              opacity: dataset?.opacity,
              selectedCategory: dataset?.color_based,
              labelConfig: dataset?.label_config,
              colorSteps: dataset?.color_steps,
              rankConfig: {
                isEnabled: dataset.label_config?.enable_ranking ?? false,
                maxRanking: dataset.label_config?.max_ranking ?? 10
              }
            }
          );
        }
      } else if (!dataset.available) {
        // For deleted datasets, some of the properties are no longer available, hence they are assigned with arbitrary default values to conform with the data type
        const deletedDatasetMetadata: DatasetDetailResponse = {
          id: dataset.id,
          name: dataset.name ?? "",
          display_name: dataset.display_name ?? "",
          country_id: "id", // arbitrary default value
          source: "bvt", // arbitrary default value
          spatial_aggregation_type:
            dataset.spatial_aggregation_type as SpatialAggregationType,
          short_description: "",
          published: false,
          created_at: "",
          updated_at: "",
          available: dataset.available,
          total_rows: 0,
          schema: []
        };

        handleAddDeletedDataset(deletedDatasetMetadata, datasetIndex);
      }
    }
  };

  // Effect to load selected configuration detail and update Redux store
  useEffect(() => {
    if (!configurationDetail) return;

    // Clear current data and add new
    processDatasets(configurationDetail);
    // Set data for spatial config
    const spatialData = mapSpatialState(configurationDetail, addFeature);
    dispatch(setSpatialState(spatialData));
    const deletedDatasets = getDeletedDatasetsName(
      configurationDetail.datasets ?? []
    );
    if (deletedDatasets) {
      showToast({
        message: `Dataset "${deletedDatasets}" cannot be loaded, please check the dataset from data management`,
        type: EToastType.WARNING
      });
    } else {
      showToast({
        message: MESSAGE.OPEN_PROJECT_SUCCESS,
        type: EToastType.SUCCESS
      });
    }

    // Close Project List Panel
    onGoBack?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationDetail]);

  return { getIndexOfDatasetSameId, processDatasets };
};
